
<template>
  <label class="checkbox">
    <input
      type="checkbox"
      :checked="shouldBeChecked"
      :disabled="isDisabled"
      @change="updateInput"
    >
    <span v-if="toggleView" 
      class="checkbox__toggle-dummy"
      :class="{'checkbox__toggle-dummy_selected': shouldBeChecked, 'checkbox__toggle-dummy_labelless': !label}"></span>
    <span 
      v-else
      class="checkbox__dummy"
      :style="checkboxColor && { color: checkboxColor }"
      :class="{'checkbox__labelless': !label, 'checkbox_disabled': showDisabledColor, 'checkbox_indeterminate': indeterminate}"
    />
    <slot />
    <base-radio-button
      v-if="withRadioButton"
      v-model="isRadioSelected"
      class="checkbox__radio-button"
      @change="updateRadioButton($event)"
    />
    <span
      class="checkbox__label"
      :style="cssProps"
    >{{ label }}</span>
  </label>
</template>

<script>
/**
 * Stylized checkbox with optional label
 */
export default {
  name: 'BaseCheckBox',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    modelValue: {
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    shouldGrayUnselected: {
      type: Boolean,
      default: true
    },
    checkboxColor: {
      type: [String, Boolean],
      default: false
    },
    trueValue: {
      default: true
    },
    falseValue: {
      default: false
    },
    indeterminate: {
      default: false
    },
    disabled: {
      default: false
    },
    withRadioButton: {
      default: false
    },
    isRadioButtonSelected: {
      default: false
    },
    toggleView: {
      default: false
    }
  },
  data() {
    return {
      value: this.modelValue,
      isRadioSelected: this.isRadioButtonSelected
    }
  },
  computed: { 
    cssProps() {
      return {
        '--label-color': this.shouldGrayUnselected ? '#b2b9be' : '#011627'
      }
    },
    shouldBeChecked () {
      return this.value === this.trueValue
    },
    showDisabledColor () {
      return this.isDisabled && !this.shouldBeChecked
    },
    isDisabled () {
      return this.disabled
    }
  },
  watch: {
    modelValue(value) {
      this.value = value
    },
    isRadioButtonSelected(value) {
      this.isRadioSelected = value
    }
  },
  methods: {
    updateInput () {
      if (this.disabled && (this.value !== this.trueValue)) {
        return
      }
      if (this.indeterminate) {
        this.indeterminate = false
        this.value = this.trueValue
      } else {
        this.value = this.value === this.trueValue ? this.falseValue : this.trueValue
      }
      this.$emit('change', this.value)
    },
    updateRadioButton (value) {
      this.$emit('radioButtonChange', value)
    }
  }
}
</script>

<style scoped lang="scss">
  .checkbox {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    user-select: none;
    cursor: pointer;

    &_disabled {
      background-color: $rfx-light-grey !important;
    }

    &_indeterminate {
      &::after {
        display: block;
        content: '';
        width: 9px;
        height: 2px;
        background: $rfx-bright-blue;
      }
    }

    input {
      display: none;

      &:checked {
        & + .checkbox__dummy {
          color: $rfx-bright-blue;
          background-color: currentColor;

          &::after {
            content: "";
            display: block;
            width: 9px;
            height: 5px;
            border-left: 2px solid #fff;
            border-bottom: 2px solid #fff;
            transform: rotate(-45deg) translateY(-1px);
            box-sizing: border-box;
          }

          & + span {
            color: $rfx-dark-march;
          }
        }

        & + .checkbox__toggle-dummy {
          border-color: $rfx-bright-turquoise;
          background-color: $rfx-bright-turquoise;

          &::before {
            opacity: 1;
            background-color: white;
            left: 20px;
          }
        }
      }

    }

    &__dummy {
      display: flex;
      width: 20px;
      height: 20px;
      flex: 0 0 20px;
      color: $rfx-silver;
      border: 2px solid currentColor;
      border-radius: 4px;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      background-color: $rfx-almost-white-lighter;
      margin-right: 8px;
    }

    &__toggle-dummy {
      cursor: pointer;
      background-color: white;
      border: 1px solid $rfx-pearl;
      width: 38px;
      height: 20px;
      border-radius: 10px;
      box-sizing: border-box;
      position: relative;
      transition: all 0.2s ease-in-out;
      margin-right: 8px;

      &_labelless {
        margin-right: 0;
      }

      &::before {
        content: '';
        display: block;
        width: 16px;
        height: 16px;
        background-color: $rfx-dark-march;
        opacity: .6;
        border-radius: 50%;
        position: absolute;
        left: 1px;
        top: 50%;
        transform: translateY(-50%);
        transition: all 0.2s ease-in-out;
      }
    }

    &__labelless {
      margin-right: 0;
    }

    &__label {
      display: block;
      color: var(--label-color);
      @include ellipsis;
    }

    &__radio-button {
      margin-right: 8px;
    }
  }
</style>
